import React, { useEffect, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import API_URL from '../config/apiConfig';
import { 
    PowerIcon, 
    EnvelopeIcon, 
    UserCircleIcon, 
    CommandLineIcon,
    ChatBubbleLeftRightIcon,
    Bars3Icon // ✅ ON UTILISE BARS3 POUR LE BURGER
} from '@heroicons/react/24/outline';

function Header({ authToken, handleLogout, toggleHistory }) {
    const location = useLocation();
    const isAdminPage = location.pathname.startsWith('/admin');
    
    const [pdfStats, setPdfStats] = useState({ used: 0, remaining: 0, total: 30 });
    const [loadingStats, setLoadingStats] = useState(true);

    const percentage = pdfStats.total > 0 ? (pdfStats.remaining / pdfStats.total) * 100 : 0;

    const getProgressColor = () => {
        if (percentage > 50) return 'bg-emerald-400';
        if (percentage > 20) return 'bg-amber-400';
        return 'bg-red-400';
    };

    const fetchCredits = () => {
        if (authToken?.userId) {
            axios.get(`${API_URL}/user-pdf-stats/${authToken.userId}`)
                .then(response => setPdfStats(response.data.data || { used: 0, remaining: 30, total: 30 }))
                .catch(error => console.error('Erreur stats PDF:', error))
                .finally(() => setLoadingStats(false));
        }
    };

    useEffect(() => {
        fetchCredits();
    }, [authToken?.userId]);

    // Ecouter l'event custom pour rafraichir les credits en temps reel
    useEffect(() => {
        const handler = () => fetchCredits();
        window.addEventListener('credits-updated', handler);
        return () => window.removeEventListener('credits-updated', handler);
    }, [authToken?.userId]);

    return (
        <header className="sticky top-0 z-50 w-full bg-[#1c2143] shadow-md border-b border-gray-800">
            <div className="w-full px-4 md:px-6"> 
                <div className="flex justify-between items-center h-16">

                    {/* --- PARTIE GAUCHE : BURGER + LOGO --- */}
                    <div className="flex items-center gap-4 md:gap-6">
                        
                        {/* ✅ BOUTON BURGER (Visible seulement si on n'est pas Admin) */}
                        {!isAdminPage && (
                            <button 
                                onClick={toggleHistory}
                                className="p-2 text-gray-300 hover:text-white hover:bg-white/10 rounded-lg transition-all"
                                title="Ouvrir l'historique"
                            >
                                <Bars3Icon className="w-7 h-7" />
                            </button>
                        )}

                        <Link to="/" className="flex items-center gap-2 hover:opacity-90 transition-opacity group">
                            <div className="w-8 h-8 bg-gradient-to-br from-[#Ceaa5b] to-[#b39045] rounded-lg flex items-center justify-center shadow-lg group-hover:shadow-[#Ceaa5b]/20 transition-all">
                                <span className="text-white font-bold text-lg">S</span>
                            </div>
                            <h1 className="text-xl md:text-2xl font-bold text-white tracking-tight">
                                Soph<span className="text-[#Ceaa5b]">IA</span>
                            </h1>
                        </Link>

                        {/* Badge Admin */}
                        {isAdminPage && (
                            <span className="hidden md:inline-block px-2 py-1 rounded bg-gray-700/50 border border-gray-600 text-xs text-gray-300 font-medium tracking-wide uppercase">
                                Admin
                            </span>
                        )}
                    </div>

                    {/* --- PARTIE DROITE : STATS + USER --- */}
                    <div className="flex items-center gap-4 md:gap-6">

                        {/* Stats Crédits (Desktop) */}
                        {!loadingStats && !isAdminPage && (
                            <div className="hidden md:flex flex-col items-end border-r border-gray-700 pr-6 h-8 justify-center">
                                <div className="flex items-center gap-3">
                                    <div className="flex flex-col items-end">
                                        <span className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold leading-none mb-1">
                                            Crédits
                                        </span>
                                        <span className="text-sm font-bold text-white leading-none">
                                            {pdfStats.remaining}
                                        </span>
                                    </div>
                                    <div className="w-1.5 h-8 bg-gray-800 rounded-full overflow-hidden flex flex-col-reverse">
                                        <div 
                                            className={`w-full transition-all duration-500 ease-out ${getProgressColor()}`} 
                                            style={{ height: `${percentage}%` }}
                                        ></div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Actions User */}
                        <div className="flex items-center gap-3">
                            {/* Bouton Switch Admin */}
                            {authToken?.isAdmin && (
                                isAdminPage ? (
                                    <Link to="/" className="p-2 bg-[#Ceaa5b] text-[#1c2143] rounded-lg hover:bg-white transition-all">
                                        <ChatBubbleLeftRightIcon className="w-5 h-5" />
                                    </Link>
                                ) : (
                                    <Link to="/admin" className="p-2 bg-gray-700 text-gray-200 rounded-lg hover:bg-gray-600 transition-all">
                                        <CommandLineIcon className="w-5 h-5" />
                                    </Link>
                                )
                            )}

                            {/* Info User */}
                            <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#151934] border border-gray-700 ml-2">
                                <UserCircleIcon className="w-5 h-5 text-gray-400" />
                                <span className="text-sm text-gray-300 font-medium pr-1">{authToken.userName || 'Utilisateur'}</span>
                            </div>

                            {/* Mail Support */}
                            <a href="mailto:contact@aiexpertys.fr" className="hidden md:block p-2 text-gray-400 hover:text-[#Ceaa5b] transition-colors">
                                <EnvelopeIcon className="w-6 h-6" />
                            </a>

                            {/* Logout */}
                            <button onClick={handleLogout} className="p-2 text-red-400 hover:text-red-300 hover:bg-red-400/10 rounded-lg transition-all">
                                <PowerIcon className="w-6 h-6" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Barre de progression Mobile */}
            <div className="md:hidden w-full h-0.5 bg-gray-800">
                <div 
                    className={`h-full transition-all duration-500 ${getProgressColor()}`} 
                    style={{ width: `${percentage}%` }}
                ></div>
            </div>
        </header>
    );
}

export default Header;